import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { QuestionView, type PublicQuestion } from './QuestionView';
import { ProgressBar } from './ProgressBar';

export interface CoachingAttemptData {
  attemptId: string;
  phase: 'dojo';
  status: 'in_progress' | 'submitted' | 'expired';
  question: PublicQuestion | null;
  progress: { cleared: number; total: number; currentCategory: string | null };
}

interface CoachingResponse {
  correct: boolean;
  correctAnswerIds: string[];
  explanation: string | null;
  categoryCleared: boolean;
  complete: boolean;
  progress: { cleared: number; total: number; currentCategory: string | null };
  next: PublicQuestion | null;
}

type Feedback =
  | { kind: 'pending' }
  | { kind: 'shown'; resp: CoachingResponse };

export function CoachingAttempt({ initial }: { initial: CoachingAttemptData }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState<PublicQuestion | null>(initial.question);
  const [progress, setProgress] = useState(initial.progress);
  const [selected, setSelected] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'pending' });
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(initial.status === 'submitted');

  if (complete) {
    return (
      <div className="page-narrow">
        <h1>🥋 Dojo complete</h1>
        <p>
          You've cleared every category. Your accuracy and badges are tracked on your
          dashboard.
        </p>
        <ProgressBar value={progress.total} max={progress.total} label="Categories cleared" />
        <div className="cta-row" style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={() => navigate(`/results/${initial.attemptId}`)}>
            See results
          </button>
        </div>
      </div>
    );
  }

  if (!question) {
    return <p className="loading">Loading next question…</p>;
  }

  const submit = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const resp = await api.post<CoachingResponse>(
        `/api/take/attempt/${initial.attemptId}/coaching-answer`,
        { questionId: question.id, selected }
      );
      setFeedback({ kind: 'shown', resp });
      setProgress(resp.progress);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (feedback.kind !== 'shown') return;
    if (feedback.resp.complete) {
      setComplete(true);
      return;
    }
    if (feedback.resp.next) {
      setQuestion(feedback.resp.next);
      setSelected([]);
      setFeedback({ kind: 'pending' });
    }
  };

  const showingFeedback = feedback.kind === 'shown';

  return (
    <div className="page-attempt">
      <header className="attempt-bar">
        <ProgressBar
          value={progress.cleared}
          max={progress.total}
          label={`Categories cleared: ${progress.cleared} / ${progress.total}`}
          variant="mastery"
        />
        <div className="timer">
          <span className="timer-label">Currently practicing</span>
          <span className="timer-value" style={{ fontSize: 18 }}>
            {progress.currentCategory ?? '—'}
          </span>
        </div>
      </header>

      <QuestionView
        question={question}
        selected={selected}
        onChange={(s) => {
          if (!showingFeedback) setSelected(s);
        }}
        index={progress.cleared}
        total={progress.total}
      />

      {showingFeedback && (
        <div className={`feedback ${feedback.resp.correct ? 'feedback-ok' : 'feedback-wrong'}`}>
          <h3>{feedback.resp.correct ? '✅ Correct!' : '❌ Not quite.'}</h3>
          {feedback.resp.explanation && <p>{feedback.resp.explanation}</p>}
          {!feedback.resp.correct && feedback.resp.correctAnswerIds.length > 0 && (
            <p className="muted">
              Correct answer{feedback.resp.correctAnswerIds.length > 1 ? 's' : ''}:{' '}
              <code>{feedback.resp.correctAnswerIds.join(', ')}</code>
            </p>
          )}
          {feedback.resp.categoryCleared && (
            <p className="muted">
              🎉 Category <code>{question.category}</code> cleared — moving on.
            </p>
          )}
          {!feedback.resp.correct && !feedback.resp.complete && (
            <p className="muted">
              Another question in <code>{question.category}</code> coming up — keep going
              until you've got it.
            </p>
          )}
        </div>
      )}

      <nav className="attempt-nav">
        {!showingFeedback ? (
          <button
            className="btn btn-primary"
            disabled={busy || selected.length === 0}
            onClick={submit}
          >
            Submit answer
          </button>
        ) : (
          <button className="btn btn-primary" onClick={next}>
            {feedback.kind === 'shown' && feedback.resp.complete ? 'Finish Dojo →' : 'Next question →'}
          </button>
        )}
      </nav>
    </div>
  );
}
